import type { ReactNode } from 'react';
import type { EditSection } from './MyNazcaImageEditorPreview';
import { MyNazcaRangeField } from './MyNazcaRangeField';

const BG_FILL_WHITE = '#ffffff';
const BG_FILL_BLACK = '#000000';

export type MyNazcaImageEditorControlsProps = {
  section: EditSection;
  variant: 'accordion-body' | 'mobile-panel' | 'tab-panel';
  lang: 'ja' | 'en';
  busy: string | null;
  cropperReady: boolean;
  autoBgRemoved: boolean;
  bgCompositeColor: string;
  brushSize: number;
  toneBrightness: number;
  toneContrast: number;
  onRotateCropper: (deg: number) => void;
  onApplyCrop: () => void;
  onAutoRemoveBackground: () => void;
  onBgCompositeColorChange: (color: typeof BG_FILL_WHITE | typeof BG_FILL_BLACK) => void;
  onBrushSizeChange: (size: number) => void;
  onToneBrightnessChange: (value: number) => void;
  onToneContrastChange: (value: number) => void;
  onToneGestureStart: () => void;
  onToneGestureEnd: () => void;
};

function wrapPanel(variant: MyNazcaImageEditorControlsProps['variant'], children: ReactNode) {
  if (variant === 'tab-panel') {
    return <div className="mynazca-app__edit-tab-panel-content">{children}</div>;
  }
  const wrapClass =
    variant === 'mobile-panel' ? 'mynazca-app__mobile-tool-panel' : 'mynazca-app__accordion-body';
  return <div className={wrapClass}>{children}</div>;
}

export function MyNazcaImageEditorControls({
  section,
  variant,
  lang,
  busy,
  cropperReady,
  autoBgRemoved,
  bgCompositeColor,
  brushSize,
  toneBrightness,
  toneContrast,
  onRotateCropper,
  onApplyCrop,
  onAutoRemoveBackground,
  onBgCompositeColorChange,
  onBrushSizeChange,
  onToneBrightnessChange,
  onToneContrastChange,
  onToneGestureStart,
  onToneGestureEnd,
}: MyNazcaImageEditorControlsProps) {
  if (section === 'crop') {
    return wrapPanel(
      variant,
      <>
        <p className="mynazca-app__muted">
          {lang === 'ja'
            ? 'プレビューでトリミング枠を調整します。'
            : 'Adjust the crop box in the preview.'}
        </p>
        <div className="mynazca-app__btnrow mynazca-app__btnrow--balanced">
          <button type="button" onClick={() => onRotateCropper(-90)} disabled={busy != null || !cropperReady}>
            {lang === 'ja' ? '左に90°' : '-90°'}
          </button>
          <button type="button" onClick={() => onRotateCropper(90)} disabled={busy != null || !cropperReady}>
            {lang === 'ja' ? '右に90°' : '+90°'}
          </button>
          <button
            type="button"
            className="mynazca-app__btn-primary"
            onClick={() => onApplyCrop()}
            disabled={busy != null || !cropperReady}
          >
            {lang === 'ja' ? '適用' : 'Apply'}
          </button>
        </div>
      </>,
    );
  }

  if (section === 'background') {
    return wrapPanel(
      variant,
      <>
        <p className="mynazca-app__muted">
          {lang === 'ja'
            ? 'プレビュー上で消しゴム、または自動削除を使います。'
            : 'Use the eraser on the preview or auto remove.'}
        </p>
        <div className="mynazca-app__btnrow mynazca-app__btnrow--block">
          <button
            type="button"
            className="mynazca-app__btn-primary"
            onClick={() => onAutoRemoveBackground()}
            disabled={busy != null || autoBgRemoved}
          >
            {lang === 'ja' ? '自動削除' : 'Auto remove'}
          </button>
        </div>
        <div className="mynazca-app__edit-setting">
          <span className="mynazca-app__field-label">{lang === 'ja' ? '背景色' : 'Background color'}</span>
          <div
            className="mynazca-app__fill-color mynazca-app__fill-color--balanced"
            role="group"
            aria-label={lang === 'ja' ? '背景色' : 'Background color'}
          >
            <button
              type="button"
              className={`mynazca-app__fill-color-btn mynazca-app__fill-color-btn--white ${bgCompositeColor === BG_FILL_WHITE ? 'mynazca-app__fill-color-btn--selected' : ''}`}
              onClick={() => onBgCompositeColorChange(BG_FILL_WHITE)}
              disabled={busy != null}
              aria-pressed={bgCompositeColor === BG_FILL_WHITE}
              aria-label={lang === 'ja' ? '白' : 'White'}
            />
            <button
              type="button"
              className={`mynazca-app__fill-color-btn mynazca-app__fill-color-btn--black ${bgCompositeColor === BG_FILL_BLACK ? 'mynazca-app__fill-color-btn--selected' : ''}`}
              onClick={() => onBgCompositeColorChange(BG_FILL_BLACK)}
              disabled={busy != null}
              aria-pressed={bgCompositeColor === BG_FILL_BLACK}
              aria-label={lang === 'ja' ? '黒' : 'Black'}
            />
          </div>
        </div>
        <MyNazcaRangeField
          label={lang === 'ja' ? '消しゴム' : 'Eraser'}
          min={4}
          max={80}
          value={brushSize}
          disabled={busy != null}
          onChange={onBrushSizeChange}
        />
      </>,
    );
  }

  return wrapPanel(
    variant,
    <>
      <MyNazcaRangeField
        label={lang === 'ja' ? '明るさ' : 'Brightness'}
        min={50}
        max={150}
        value={toneBrightness}
        disabled={busy != null}
        onPointerDown={onToneGestureStart}
        onPointerUp={onToneGestureEnd}
        onFocus={onToneGestureStart}
        onBlur={onToneGestureEnd}
        onKeyUp={onToneGestureEnd}
        onChange={onToneBrightnessChange}
      />
      <MyNazcaRangeField
        label={lang === 'ja' ? 'コントラスト' : 'Contrast'}
        min={50}
        max={150}
        value={toneContrast}
        disabled={busy != null}
        onPointerDown={onToneGestureStart}
        onPointerUp={onToneGestureEnd}
        onFocus={onToneGestureStart}
        onBlur={onToneGestureEnd}
        onKeyUp={onToneGestureEnd}
        onChange={onToneContrastChange}
      />
    </>,
  );
}
